import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Modal, Form, Button, ProgressBar, Row, Col } from 'react-bootstrap';
import { Get, Post, Put, DeleteSingle } from '../../utils/httpProvider';
import { ANNUAL_JOB_KANBAN_API, BaseGatewayImage } from '../../utils/apiUrl';
import { toast } from 'react-toastify';
import moment from 'moment';
import DatePicker from '@app/components/CustomDatePicker';
import axios from 'axios';
import { getToken } from '../../utils/helper';

const COLUMN_STYLE_MAP = {
    0: { headerBg: '#e1e5f2', headerText: '#333333', border: '#c4ccdd', bodyBg: '#f7f9fc', label: 'Cần làm', color: 'secondary' },
    1: { headerBg: '#d3e5ff', headerText: '#245b8f', border: '#aac6ff', bodyBg: '#f3f7ff', label: 'Đang thực hiện', color: 'primary' },
    3: { headerBg: '#cef4e4', headerText: '#247a55', border: '#a0e0c6', bodyBg: '#f1fbf6', label: 'Hoàn thành', color: 'success' },
    4: { headerBg: '#f8d7da', headerText: '#721c24', border: '#f5c6cb', bodyBg: '#fff5f5', label: 'Quá hạn', color: 'danger' },
};

const ANNUAL_TASK_STATUS_CONST = [
    { value: 0, label: 'Cần làm', color: 'secondary' },
    { value: 1, label: 'Đang thực hiện', color: 'primary' },
    { value: 3, label: 'Hoàn thành', color: 'success' },
    { value: 4, label: 'Quá hạn', color: 'danger' }
];

const toDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return '';
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    return `${d}/${m}/${y}`;
};

const AnnualJobKanban = ({ isNested = false }) => {
    const [rawDetails, setRawDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draggingId, setDraggingId] = useState(null);
    const containerRef = React.useRef(null);

    // Modal Progress
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [progressForm, setProgressForm] = useState({
        reportDate: moment().format('YYYY-MM-DD'),
        achievedKPI: 0,
        note: ''
    });

    // Modal Evidence
    const [showEvidenceModal, setShowEvidenceModal] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Modal Detail Progress
    const [showDetailProgressModal, setShowDetailProgressModal] = useState(false);

    useEffect(() => {
        fetchKanbanBoard();
    }, []);

    const normalizeTaskCard = (detail, registration = {}) => {
        const rawProgresses = detail.Progresses ?? detail.progresses;
        const progresses = (rawProgresses && rawProgresses.$values ? rawProgresses.$values : rawProgresses) || [];

        const rawEvidenceFiles = detail.EvidenceFiles ?? detail.evidenceFiles;
        const evidenceFiles = (rawEvidenceFiles && rawEvidenceFiles.$values ? rawEvidenceFiles.$values : rawEvidenceFiles) || [];

        return {
            id: detail.Id ?? detail.id,
            annualJobId: detail.AnnualJobId ?? detail.annualJobId,
            registeredKPI: detail.RegisteredKPI ?? detail.registeredKPI,
            registeredScore: detail.RegisteredScore ?? detail.registeredScore ?? 0,
            currentKPI: detail.CurrentKPI ?? detail.currentKPI ?? 0,
            currentScore: detail.CurrentScore ?? detail.currentScore ?? 0,
            status: detail.Status ?? detail.status ?? 0,
            annualJobName: detail.AnnualJobName ?? detail.annualJobName ?? detail.JobName ?? detail.jobName,
            annualJobUnit: detail.AnnualJobUnit ?? detail.annualJobUnit ?? detail.Unit ?? detail.unit,
            annualJobWeight: detail.AnnualJobWeight ?? detail.annualJobWeight ?? detail.Weight ?? detail.weight,
            annualJobBaseQuantity: detail.AnnualJobBaseQuantity ?? detail.annualJobBaseQuantity ?? detail.BaseQuantity ?? detail.baseQuantity ?? 1,
            regName: registration.regName ?? detail.RegName ?? detail.regName,
            regStartDate: registration.regStartDate ?? detail.RegStartDate ?? detail.regStartDate,
            regEndDate: registration.regEndDate ?? detail.RegEndDate ?? detail.regEndDate,
            endDate: detail.EndDate ?? detail.endDate,
            actualEndDate: detail.ActualEndDate ?? detail.actualEndDate,
            progresses: progresses.map(p => ({
                id: p.Id ?? p.id,
                reportDate: p.ReportDate ?? p.reportDate,
                achievedKPI: p.AchievedKPI ?? p.achievedKPI,
                achievedScore: p.AchievedScore ?? p.achievedScore,
                note: p.Note ?? p.note
            })),
            evidenceFiles: evidenceFiles.map(ev => ({
                id: ev.Id ?? ev.id,
                fileName: ev.FileName ?? ev.fileName,
                filePath: ev.FilePath ?? ev.filePath,
                fileSize: ev.FileSize ?? ev.fileSize
            }))
        };
    };

    const fetchKanbanBoard = async () => {
        setLoading(true);
        try {
            const res = await Get(ANNUAL_JOB_KANBAN_API.GET_KANBAN_BOARD);
            if (res) {
                const dataObj = res.data ?? res.Data ?? res;
                const responseItems = Array.isArray(dataObj) ? dataObj : (dataObj.$values || []);
                let allDetails = [];

                const isFlatResponse = responseItems.some(item =>
                    (item.AnnualJobName ?? item.annualJobName) !== undefined ||
                    (item.RegisteredKPI ?? item.registeredKPI) !== undefined
                );

                if (isFlatResponse) {
                    allDetails = responseItems.map(item => normalizeTaskCard(item));
                    setRawDetails(allDetails);
                    return;
                }

                responseItems.forEach(reg => {
                    const rawDetailsList = reg.Details ?? reg.details;
                    const details = (rawDetailsList && rawDetailsList.$values ? rawDetailsList.$values : rawDetailsList) || [];
                    
                    details.forEach(d => {
                        allDetails.push(normalizeTaskCard(d, {
                            regName: reg.Name ?? reg.name,
                            regStartDate: reg.StartDate ?? reg.startDate,
                            regEndDate: reg.EndDate ?? reg.endDate
                        }));
                    });
                });

                setRawDetails(allDetails);
            }
        } catch (error) {
            console.error('Error fetching kanban:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMoveTask = async (taskId, newStatus) => {
        try {
            await Put(`${ANNUAL_JOB_KANBAN_API.UPDATE_STATUS}/${taskId}/${newStatus}`);
            toast.success('Cập nhật trạng thái thành công');
            fetchKanbanBoard();
        } catch (error) {
            toast.error(error.response?.data || 'Lỗi khi cập nhật trạng thái');
            fetchKanbanBoard();
        }
    };

    const handleDragStart = (task, e) => {
        if (task.status === 4) {
            e.preventDefault();
            return;
        }
        setDraggingId(task.id);
    };
    const handleDragEnd = () => setDraggingId(null);
    const handleDropOnColumn = async (newStatus) => {
        if (draggingId !== null) {
            const task = rawDetails.find(d => d.id === draggingId);
            if (!task) return;
            if (newStatus === 4 || task.status === 4) {
                toast.warning('Không thể kéo thẻ ra/vào cột Quá hạn.');
                setDraggingId(null);
                return;
            }
            if (task.status !== newStatus) {
                await handleMoveTask(draggingId, newStatus);
            }
            setDraggingId(null);
        }
    };

    const handleDragOverContainer = (e) => {
        if (!containerRef.current) return;
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;

        const scrollThreshold = 80;
        const maxSpeed = 25;

        if (x > rect.width - scrollThreshold) {
            const ratio = (x - (rect.width - scrollThreshold)) / scrollThreshold;
            container.scrollLeft += Math.ceil(Math.min(ratio, 1) * maxSpeed);
        } else if (x < scrollThreshold) {
            const ratio = (scrollThreshold - x) / scrollThreshold;
            container.scrollLeft -= Math.ceil(Math.min(ratio, 1) * maxSpeed);
        }
    };

    const openProgressModal = (task) => {
        if (task.status === 4 || task.status === 3) return;
        setSelectedTask(task);
        setProgressForm({
            reportDate: moment().format('YYYY-MM-DD'),
            achievedKPI: 0,
            note: ''
        });
        setShowProgressModal(true);
    };

    const submitProgress = async (e) => {
        e.preventDefault();
        try {
            await Post(`${ANNUAL_JOB_KANBAN_API.ADD_PROGRESS}/${selectedTask.id}`, null, progressForm);
            toast.success('Thêm tiến độ thành công');
            setShowProgressModal(false);
            fetchKanbanBoard();
        } catch (error) {
            toast.error('Lỗi khi thêm tiến độ');
        }
    };

    const openEvidenceModal = (task) => {
        setSelectedTask(task);
        setShowEvidenceModal(true);
    };

    const openDetailProgressModal = (task) => {
        setSelectedTask(task);
        setShowDetailProgressModal(true);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (selectedTask.status === 4) {
            toast.error('Công việc đã hết hạn không thể tải minh chứng');
            return;
        }

        setUploading(true);
        const uploadForm = new FormData();
        uploadForm.append('file', file);
        uploadForm.append('folder', 'annual_evidence');

        try {
            const token = await getToken();
            const resUpload = await axios.post(`${BaseGatewayImage}/api/v1/FileManager/Upload`, uploadForm, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            if (resUpload.data) {
                await Post(`${ANNUAL_JOB_KANBAN_API.ADD_EVIDENCE_FILE}/${selectedTask.id}`, null, {
                    fileName: file.name,
                    filePath: resUpload.data.filePath || resUpload.data.thumbPath,
                    fileSize: file.size
                });
                toast.success('Tải lên minh chứng thành công');
                fetchKanbanBoard();
                
                // Update selectedTask evidence files in state to show immediately in modal
                setSelectedTask(prev => {
                    const newFiles = [...prev.evidenceFiles];
                    newFiles.push({
                        id: Math.random(), // temp id
                        fileName: file.name,
                        filePath: resUpload.data.filePath
                    });
                    return { ...prev, evidenceFiles: newFiles };
                });
            }
        } catch (error) {
            toast.error('Tải lên thất bại');
        } finally {
            setUploading(false);
            e.target.value = null; // reset input
        }
    };

    const handleDeleteEvidence = async (fileId) => {
        if (window.confirm('Bạn muốn xóa minh chứng này?')) {
            try {
                await DeleteSingle(`${ANNUAL_JOB_KANBAN_API.DELETE_EVIDENCE_FILE}/${fileId}`);
                toast.success('Xóa minh chứng thành công');
                fetchKanbanBoard();
                
                setSelectedTask(prev => {
                    let newFiles = [...prev.evidenceFiles];
                    newFiles = newFiles.filter(f => f.id !== fileId);
                    return { ...prev, evidenceFiles: newFiles };
                });
            } catch (error) {
                toast.error('Xóa thất bại');
            }
        }
    };

    const handleDeleteProgress = async (progressId) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa tiến độ này?')) {
            try {
                await DeleteSingle(`${ANNUAL_JOB_KANBAN_API.DELETE_PROGRESS}/${progressId}`);
                toast.success('Xóa tiến độ thành công');
                fetchKanbanBoard();

                setSelectedTask(prev => {
                    if (!prev) return null;
                    const updatedProgresses = prev.progresses.filter(p => p.id !== progressId);
                    const deletedProg = prev.progresses.find(p => p.id === progressId);
                    const kpiDiff = deletedProg ? deletedProg.achievedKPI : 0;
                    const scoreDiff = deletedProg ? deletedProg.achievedScore : 0;
                    return {
                        ...prev,
                        progresses: updatedProgresses,
                        currentKPI: Math.max(0, prev.currentKPI - kpiDiff),
                        currentScore: Math.max(0, prev.currentScore - scoreDiff)
                    };
                });
            } catch (error) {
                toast.error(error.response?.data || 'Xóa tiến độ thất bại');
            }
        }
    };


    const assignmentsByStatus = useMemo(() => {
        const result = {};
        ANNUAL_TASK_STATUS_CONST.forEach(col => {
            result[col.value] = rawDetails.filter(d => Number(d.status) === Number(col.value));
        });
        return result;
    }, [rawDetails]);

    return (
        <div className={isNested ? "" : "container-fluid"}>
            {!isNested && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 border-bottom pb-3">
                    <div className="mb-3 mb-md-0">
                        <h2 className="mb-1 text-dark fw-bold">Tiến độ Công việc Thường niên</h2>
                        <p className="text-muted mb-0" style={{ fontSize: '15px' }}>Xem và quản lý tiến độ các công việc thường niên của bạn</p>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-5">Đang tải bảng...</div>
            ) : (
                <Row ref={containerRef} onDragOver={handleDragOverContainer} className="flex-nowrap overflow-auto pb-3" style={{ minHeight: '600px' }}>
                    {ANNUAL_TASK_STATUS_CONST.map(col => {
                        const list = assignmentsByStatus[col.value] || [];
                        const styleCfg = COLUMN_STYLE_MAP[col.value] || COLUMN_STYLE_MAP[0];

                        return (
                            <Col key={col.value} md={3} style={{ minWidth: '320px' }} className="d-flex flex-column">
                                <div
                                    className="card mb-3 shadow-sm flex-grow-1 d-flex flex-column"
                                    style={{ borderColor: styleCfg.border, borderWidth: 1, borderStyle: 'solid', backgroundColor: '#fff' }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => handleDropOnColumn(col.value)}
                                >
                                    <div
                                        className="card-header d-flex align-items-center"
                                        style={{ backgroundColor: styleCfg.headerBg, borderBottomColor: styleCfg.border, borderBottomStyle: 'solid', borderBottomWidth: 1, color: styleCfg.headerText, fontWeight: 700, fontSize: 17, height: 60 }}
                                    >
                                        <span>{col.label}</span>
                                        <div style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>{list.length}</div>
                                        <div style={{ width: 28 }}></div>
                                    </div>

                                    <div
                                        className="card-body p-2 flex-grow-1"
                                        style={{ minHeight: 450, backgroundColor: styleCfg.bodyBg }}
                                    >
                                        {list.map(task => {
                                            const kpiPercent = (Number(task.registeredKPI) || 0) > 0 ? ((Number(task.currentKPI) || 0) / (Number(task.registeredKPI) || 0)) * 100 : 0;
                                            const isBeingDragged = draggingId === task.id;

                                            return (
                                                <Card
                                                    key={task.id}
                                                    className="mb-2 shadow-sm"
                                                    draggable={task.status !== 4}
                                                    onDragStart={(e) => handleDragStart(task, e)}
                                                    onDragEnd={handleDragEnd}
                                                    style={{
                                                        opacity: isBeingDragged ? 0.5 : 1,
                                                        cursor: task.status === 4 ? 'not-allowed' : 'grab',
                                                        borderLeft: task.status === 4 ? `4px solid ${styleCfg.headerText}` : undefined
                                                    }}
                                                >
                                                    <Card.Body className="p-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <small className="text-muted text-truncate" style={{ maxWidth: '80%', fontWeight: 500 }}>
                                                                {task.regName}
                                                            </small>
                                                            {task.status === 4 && <Badge variant="danger">Hết hạn</Badge>}
                                                        </div>
                                                        <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: '15px' }}>{task.annualJobName}</h6>
                                                        
                                                        <div style={{ fontSize: 13, color: '#555', lineHeight: '1.6' }} className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <span>Tiến độ: <span className="fw-bold text-primary">{task.currentKPI} / {task.registeredKPI} {task.annualJobUnit}</span></span>
                                                                <span className="fw-bold text-dark">{kpiPercent.toFixed(0)}%</span>
                                                            </div>
                                                            <ProgressBar 
                                                                variant={kpiPercent >= 100 ? "success" : "primary"} 
                                                                now={kpiPercent} 
                                                                style={{ height: '6px', borderRadius: '3px' }} 
                                                            />
                                                        </div>

                                                        <div className="mb-2" style={{ fontSize: 13, color: '#444' }}>
                                                            Định mức: <strong>{task.annualJobBaseQuantity} {task.annualJobUnit} = {task.annualJobWeight} điểm</strong>
                                                        </div>

                                                        <div className="mb-3 text-muted" style={{ fontSize: 13 }}>
                                                            Điểm quy đổi: <span className="fw-bold text-success">{task.currentScore.toFixed(2)}</span> / {task.registeredScore.toFixed(2)}
                                                        </div>

                                                        {task.regStartDate && (
                                                            <div style={{ fontSize: '12px' }} className="text-primary fw-bold mb-2">
                                                                📅 Bắt đầu: {toDisplayDate(task.regStartDate)}
                                                            </div>
                                                        )}

                                                        {(task.endDate || task.regEndDate) && (
                                                            <div style={{ fontSize: '12px' }} className="text-danger fw-bold mb-2">
                                                                ⏰ Hạn chót: {toDisplayDate(task.endDate ?? task.regEndDate)}
                                                            </div>
                                                        )}

                                                        <div className="d-flex flex-column border-top pt-3 mt-2" style={{ gap: 8 }}>
                                                            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                                                {ANNUAL_TASK_STATUS_CONST.filter(c => c.value !== task.status && c.value !== 4).map(c => (
                                                                    <Button
                                                                        key={c.value}
                                                                        variant={`outline-${c.color}`}
                                                                        style={{ fontSize: 12, padding: '4px 10px', fontWeight: 600 }}
                                                                        onClick={() => {
                                                                            if (task.status !== 4) {
                                                                                handleMoveTask(task.id, c.value);
                                                                            }
                                                                        }}
                                                                        disabled={task.status === 4}
                                                                    >
                                                                        → {c.label}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                                                {task.status !== 4 && task.status !== 3 && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        style={{ fontSize: 12, padding: '4px 12px', fontWeight: 600 }}
                                                                        onClick={() => openProgressModal(task)}
                                                                    >
                                                                        + Báo cáo
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    style={{ fontSize: 12, padding: '4px 12px', fontWeight: 600 }}
                                                                    onClick={() => openEvidenceModal(task)}
                                                                >
                                                                    📎 Minh chứng
                                                                </Button>
                                                                <Button
                                                                    variant="outline-info"
                                                                    style={{ fontSize: 12, padding: '4px 12px', fontWeight: 600 }}
                                                                    onClick={() => openDetailProgressModal(task)}
                                                                >
                                                                    Chi tiết
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Modal Báo cáo Tiến độ */}
            <Modal show={showProgressModal} onHide={() => setShowProgressModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Báo cáo tiến độ</Modal.Title>
                </Modal.Header>
                <Form onSubmit={submitProgress}>
                    <Modal.Body>
                        {selectedTask && (
                            <div className="mb-4 bg-light p-3 rounded">
                                <h6 className="fw-bold text-primary mb-2">{selectedTask.annualJobName}</h6>
                                <p className="text-muted small mb-0">Khối lượng đăng ký: <strong>{selectedTask.registeredKPI} {selectedTask.annualJobUnit}</strong></p>
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Ngày báo cáo</Form.Label>
                            <DatePicker 
                                className="form-control"
                                selected={progressForm.reportDate ? new Date(progressForm.reportDate) : null}
                                onChange={(date) => setProgressForm({...progressForm, reportDate: date ? moment(date).format('YYYY-MM-DD') : ''})}
                                required
                                dateFormat="dd/MM/yyyy"
                                placeholderText="dd/MM/yyyy"
                                isClearable
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Khối lượng đạt được (KPI)</Form.Label>
                            <Form.Control
                                type="number"
                                step="1"
                                required
                                value={progressForm.achievedKPI === 0 ? '' : progressForm.achievedKPI}
                                onChange={e => {
                                    const val = e.target.value;
                                    setProgressForm({...progressForm, achievedKPI: val === '' ? 0 : parseInt(val) || 0})
                                }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Ghi chú</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={progressForm.note}
                                onChange={e => setProgressForm({...progressForm, note: e.target.value})}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowProgressModal(false)}>Hủy</Button>
                        <Button variant="primary" type="submit">Lưu tiến độ</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal Minh chứng */}
            <Modal show={showEvidenceModal} onHide={() => setShowEvidenceModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Tệp minh chứng</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTask && selectedTask.status !== 4 && (
                        <div className="mb-4 bg-light p-3 rounded">
                            <Form.Group>
                                <Form.Label className="fw-bold text-primary">Tải lên file mới</Form.Label>
                                <Form.Control 
                                    type="file" 
                                    onChange={handleFileUpload}
                                    disabled={uploading}
                                />
                                {uploading && <small className="text-muted mt-1 d-block">Đang tải lên...</small>}
                            </Form.Group>
                        </div>
                    )}
                    
                    <h6 className="fw-bold mb-3">Danh sách tệp đã tải lên</h6>
                    {selectedTask && selectedTask.evidenceFiles && selectedTask.evidenceFiles.length > 0 ? (
                        <div className="list-group">
                            {selectedTask.evidenceFiles.map((file, i) => (
                                <div key={file.id || i} className="list-group-item d-flex justify-content-between align-items-center border-0 border-bottom">
                                    <div>
                                        <i className="fas fa-file-alt text-primary me-3 fs-4 align-middle"></i>
                                        <a href={`${BaseGatewayImage}/${file.filePath}`} target="_blank" rel="noreferrer" className="text-decoration-none text-dark fw-medium">
                                            {file.fileName}
                                        </a>
                                    </div>
                                    {selectedTask.status !== 4 && (
                                        <Button variant="link" className="text-danger p-0" onClick={() => handleDeleteEvidence(file.id)}>
                                            <i className="fas fa-trash fs-5"></i>
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted fst-italic">Chưa có minh chứng nào.</p>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal Chi tiết Tiến độ */}
            <Modal show={showDetailProgressModal} onHide={() => setShowDetailProgressModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Chi tiết tiến độ công việc</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTask && (
                        <>
                            <div className="mb-4 bg-light p-3 rounded">
                                <h6 className="fw-bold text-primary mb-2">{selectedTask.annualJobName}</h6>
                                <div className="row text-muted small">
                                    <div className="col-md-6 mb-1">
                                        Đã báo cáo: <strong className="text-success">{selectedTask.currentKPI} / {selectedTask.registeredKPI} {selectedTask.annualJobUnit}</strong>
                                    </div>
                                    <div className="col-md-6 mb-1">
                                        Điểm quy đổi: <strong className="text-success">{selectedTask.currentScore.toFixed(2)} / {selectedTask.registeredScore.toFixed(2)}</strong>
                                    </div>
                                </div>
                            </div>
                            
                            <h6 className="fw-bold mb-3">Lịch sử báo cáo tiến độ</h6>
                            {selectedTask.progresses && selectedTask.progresses.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped align-middle" style={{ fontSize: '14px' }}>
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '50px' }} className="text-center">STT</th>
                                                <th style={{ width: '120px' }}>Ngày báo cáo</th>
                                                <th style={{ width: '100px' }} className="text-center">KPI đạt</th>
                                                <th style={{ width: '120px' }} className="text-center">Điểm quy đổi</th>
                                                <th>Ghi chú</th>
                                                {selectedTask.status !== 4 && <th style={{ width: '80px' }} className="text-center">Hành động</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedTask.progresses.map((p, idx) => (
                                                <tr key={p.id || idx}>
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td>{toDisplayDate(p.reportDate)}</td>
                                                    <td className="text-center fw-bold text-primary">{p.achievedKPI}</td>
                                                    <td className="text-center fw-bold text-success">{p.achievedScore ? p.achievedScore.toFixed(2) : '0.00'}</td>
                                                    <td style={{ whiteSpace: 'pre-line' }}>{p.note}</td>
                                                    {selectedTask.status !== 4 && (
                                                        <td className="text-center">
                                                            <Button 
                                                                variant="link" 
                                                                className="text-danger p-0" 
                                                                onClick={() => handleDeleteProgress(p.id)}
                                                                title="Xóa tiến độ"
                                                            >
                                                                <i className="fas fa-trash fs-5"></i>
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="text-muted fst-italic">Chưa có báo cáo tiến độ nào.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailProgressModal(false)}>Đóng</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AnnualJobKanban;
